/* Turning a kernel track into a renderable contact.
 *
 * The symbol is composed HERE, in the client, from the domain and affiliation the Gateway sends.
 * Core never emits a SIDC — a symbol code is a rendering decision, and putting one inside the
 * physics engine is the coupling AD-06 exists to prevent. It also means switching the whole
 * display between 2525E and APP-6(E) is a client setting, which is exactly what it should be.
 *
 * ── The rule that shapes this file ─────────────────────────────────────────
 *
 * **A field the Gateway omitted must render as unknown, never as a default.** The Gateway gates
 * every field on the classification level that earns it: below DOMAIN level there is no `domain`,
 * and before a track history exists there is no course or speed. Filling those in with a plausible
 * default would draw a confident symbol from information the simulation says nobody has — the
 * precise failure the three-tier knowledge model exists to prevent.
 */

import { Affiliation, Entity, SymbolSet, sidc } from './sidc'
import type { Domain, OrbisContact } from '../data/types'

/** Track as it arrives from the Gateway. Optional fields are optional *because they are unknown*. */
export interface LiveTrack {
  id: string
  track: string
  lat: number
  lon: number
  uncertaintyNm: number
  confidence: number
  classification: string
  affiliation: string
  ageSec: number
  observations: number
  passive: boolean
  detectedBy: string[]
  emitting: boolean
  domain?: string
  courseDeg?: number
  speedKt?: number
  altitudeFt?: number
}

/** Assessed affiliation → 2525E affiliation digit.
 *
 *  `pending` maps to `pending`, not to `unknown`: they are different states. Pending means the
 *  system has not finished evaluating; unknown means it evaluated and could not tell. An operator
 *  waits on the first and acts on the second. */
const AFFILIATION: Record<string, Affiliation> = {
  pending: Affiliation.pending,
  unknown: Affiliation.unknown,
  assumed_friend: Affiliation.assumedFriend,
  friend: Affiliation.friend,
  neutral: Affiliation.neutral,
  suspect: Affiliation.suspect,
  hostile: Affiliation.hostile,
}

const SYMBOL_SET: Record<string, SymbolSet> = {
  air: SymbolSet.air,
  surface: SymbolSet.seaSurface,
  subsurface: SymbolSet.subsurface,
  land: SymbolSet.landUnit,
  space: SymbolSet.space,
}

/** Domain-level entity codes.
 *
 *  Deliberately generic. A track classified only to DOMAIN is an air *something*, and drawing a
 *  fighter symbol for it would assert a classification the simulation has not made. The specific
 *  symbol arrives when the classification does. */
const DOMAIN_ENTITY: Record<string, string> = {
  air: Entity.fixedWing,
  surface: Entity.surfaceCombatant,
  subsurface: Entity.submarine,
  land: Entity.airDefence,
  space: Entity.fixedWing,
}

export function trackToContact(t: LiveTrack): OrbisContact {
  const affiliation = AFFILIATION[t.affiliation] ?? Affiliation.pending
  // No domain means the track has not been classified that far. `unknown` symbol set draws the
  // 2525E unknown frame, which is the honest symbol for an unclassified return.
  const domain = (t.domain ?? 'unknown') as Domain | 'unknown'
  // An unclassified return still needs a symbol set to render at all. `air` is used as the
  // carrier, and the PENDING/UNKNOWN affiliation digit is what makes the frame read as unknown —
  // which is the honest symbol, since 2525E encodes "not identified" in the frame, not the set.
  const set = SYMBOL_SET[domain] ?? SymbolSet.air
  const entity = DOMAIN_ENTITY[domain] ?? Entity.fixedWing

  return {
    id: t.id,
    track: t.track,
    affiliation,
    domain: (t.domain ?? 'air') as Domain,
    sidc: sidc({ affiliation, set, entity }),
    lat: t.lat,
    lon: t.lon,
    courseDeg: t.courseDeg,
    speedKt: t.speedKt,
    uncertaintyNm: t.uncertaintyNm,
    classification: t.classification,
    confidence: t.confidence,
    ageSec: t.ageSec,
    detectedBy: describeDetection(t),
    emitting: t.emitting,
  }
}

/** How the track was obtained, in the operator's terms.
 *
 *  Reads "ELINT" for a passive-only hold and names the contributing platforms otherwise. This is
 *  the "who is seeing this?" answer — the first thing an operator asks about a track they cannot
 *  see from their own platform. */
function describeDetection(t: LiveTrack): string {
  const how = t.passive ? 'ESM' : 'RADAR'
  const n = t.detectedBy.length
  if (n === 0) return how
  return n === 1 ? `${how} · 1 platform` : `${how} · ${n} platforms`
}
